import { JsonObject, JsonProperty } from "typescript-json-serializer";
import { PortalConfiguration } from "./portal-configuration";
import {WidgetConfiguration} from "./widget-configuration";
import {PortalItemConfiguration} from "./portal-item-configuration";

@JsonObject()
export class TemplateOptionsConfiguration {
    /**
     * When `false`, the attribution is not displayed on the printout.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#attributionEnabled)
     */
    @JsonProperty()
    declare attributionEnabled?: boolean;
    /**
     * The text used for the author if the specified layout contains an author text element.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#author)
     */
    @JsonProperty()
    declare author?: string;
    /**
     * The text used for the copyright if the specified layout contains an copyright text element.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#copyright)
     */
    @JsonProperty()
    declare copyright?: string;
    /**
     * Use this prop to display text fields for custom text elements from the print service.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#customTextElements)
     */
    @JsonProperty()
    declare customTextElements?: any[];
    /**
     * Resolution in dots per inch.
     *
     * @default 96
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#dpi)
     */
    @JsonProperty()
    declare dpi?: string;
    /**
     * This property only applies when the [layout](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#layout) value is `map-only`.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#fileName)
     */
    @JsonProperty()
    declare fileName?: string;
    /**
     * When true, the feature's attributes are included in feature collection layers even when they are not needed for rendering.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#forceFeatureAttributes)
     */
    @JsonProperty()
    declare forceFeatureAttributes?: boolean;
    /**
     * The output format for the printed map.
     *
     * @default pdf
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#format)
     */
    @JsonProperty()
    declare format?: "pdf" | "png32" | "png8" | "jpg" | "gif" | "eps" | "svg" | "svgz";
    /**
     * Map height.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#height)
     */
    @JsonProperty()
    declare height?: number;
    /**
     * When `true`, tables will be included in the printout request.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#includeTables)
     */
    @JsonProperty()
    declare includeTables?: boolean;
    /**
     * The layout used for the print output.
     *
     * @default letter-ansi-a-landscape
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#layout)
     */
    @JsonProperty()
    declare layout?: | "map-only"
        | "a3-landscape"
        | "a3-portrait"
        | "a4-landscape"
        | "a4-portrait"
        | "letter-ansi-a-landscape"
        | "letter-ansi-a-portrait"
        | "tabloid-ansi-b-landscape"
        | "tabloid-ansi-b-portrait";
    /**
     * A custom layout hosted as a [portal item](https://developers.arcgis.com/javascript/latest/api-reference/esri-portal-PortalItem.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#layoutItem)
     */
    @JsonProperty()
    declare layoutItem?: PortalItemConfiguration;
    /**
     * When `false`, the legend is not displayed on the printout.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#legendEnabled)
     */
    @JsonProperty()
    declare legendEnabled?: boolean;
    /**
     * When `true`, the north arrow will be included on the printout.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#northArrowEnabled)
     */
    @JsonProperty()
    declare northArrowEnabled?: boolean;
    /**
     * The map scale of the printed map.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#scale)
     */
    @JsonProperty()
    declare scale?: number;
    /**
     * Define whether the printed map should preserve map scale or map extent.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#scaleEnabled)
     */
    @JsonProperty()
    declare scaleEnabled?: boolean;
    /**
     * The text used for the map title if the specified layout contains a title text element.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#title)
     */
    @JsonProperty()
    declare title?: string;
    /**
     * Map width.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-TemplateOptions.html#width)
     */
    @JsonProperty()
    declare width?: number;
}

@JsonObject()
export class PrintConfiguration extends WidgetConfiguration {
    /**
     * Specify the print output file format(s) that the user can select based on the options available from the print service.
     *
     * @default "all"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print.html#allowedFormats)
     */
    @JsonProperty()
    declare allowedFormats?: string | string[];
    /**
     * Specify the print output layout(s) that the user can select based on the options available from the print service.
     *
     * @default "all"
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print.html#allowedLayouts)
     */
    @JsonProperty()
    declare allowedLayouts?: string | string[];
    /**
     * This option allows passing extra parameters (in addition to [templateOptions](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print.html#templateOptions)) to the print (export webmap) requests.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print.html#extraParameters)
     */
    @JsonProperty()
    declare extraParameters?: any;
    /**
     * Indicates the heading level to use for the "Exported files" text where users can access the exported map printout.
     *
     * @default 3
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print.html#headingLevel)
     */
    @JsonProperty()
    declare headingLevel?: number;
    /**
     * The widget's default CSS icon class.
     *
     * @deprecated since 4.27. Use {@link module:esri/widgets/Print#icon icon} instead.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print.html#iconClass)
     */
    @JsonProperty()
    declare iconClass?: string;
    /**
     * Indicates whether or not to include [defaultTemplates](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print-PrintViewModel.html#defaultTemplates).
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print.html#includeDefaultTemplates)
     */
    @JsonProperty()
    declare includeDefaultTemplates?: boolean;
    /**
     * It is possible to search a specified portal instance's [locator services](https://enterprise.arcgis.com/en/portal/latest/administer/windows/configure-portal-to-geocode-addresses.htm).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print.html#portal)
     */
    @JsonProperty()
    declare portal?: PortalConfiguration;
    /**
     * The URL of the REST endpoint of the Export Web Map Task.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print.html#printServiceUrl)
     */
    @JsonProperty()
    declare printServiceUrl?: string;
    /**
     * An object containing an array of `customTextElements` name-value pair objects for each print template in a custom print service.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print.html#templateCustomTextElements)
     */
    @JsonProperty()
    declare templateCustomTextElements?: any;
    /**
     * Defines the layout template options used by the [Print](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print.html) widget to generate the print page.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print.html#templateOptions)
     */
    @JsonProperty()
    declare templateOptions?: TemplateOptionsConfiguration;
    /**
     * A reference to the [MapView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print.html#view)
     */
    // @JsonProperty()
    // declare view?: MapViewProperties;
    /**
     * The view model for this widget.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Print.html#viewModel)
     */
    // @JsonProperty()
    // declare viewModel?: PrintViewModelProperties;

    /**
     * *Geo.js Properties*
     * Map Frame size per layout in millimeter, e.g. A4_hoch: [183.8345, 264.1301]
     */
    @JsonProperty()
    declare exportDirectory: string;
    /**
     * *Geo.js Properties*
     * Map Frame size per layout in millimeter, e.g. A4_hoch: [183.8345, 264.1301]
     */
    @JsonProperty()
    declare layouts: Map<string, number[]>;
    /**
     * *Geo.js Properties*
     * URL zum AGE Server (wird im files/web.config verwendet)
     */
    @JsonProperty()
    declare printServiceResultUrl?: string;
    /**
     * *Geo.js Properties*
     * Liste der verwendeten Massstäbe im Druck
     */
    @JsonProperty()
    declare scales: number[];
    constructor() {
        super();
    }
}
